'use client';

import { useEffect, useRef } from 'react';
import { useIntersectionObserver } from '@/hooks/use-intersection-observer';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useStore } from '@/store/store';
import { sendExposureEvent } from '@/tracking/amplitude/events/general/experiment-exposure';

type ExposureEffectsProps = {
    flagKey: string;
    shouldFireWhenIntersects?: boolean;
};

/**
 *
 * Anchored at page level (not on the zone) so both arms are captured: treatment and control
 * Only logged-in users are exposed (the feature surface is logged-in only), and
 * `sendExposureEvent` dedupes via a module-level Set so a single exposure persists
 * across client-side navigation for the whole session.
 */
export const ExposureEffects = ({ flagKey, shouldFireWhenIntersects = false }: ExposureEffectsProps) => {
    const { experiments, isLoggedIn } = useGlobalContext();
    const { sendEvent } = useStore((state) => state.tracking);
    const [ref, isInView] = useIntersectionObserver<HTMLDivElement>({
        rootMargin: '0px'
    });
    const hasFired = useRef(false);

    useEffect(() => {
        const intersectionCondition = shouldFireWhenIntersects && (!isInView || hasFired.current);
        if (!isLoggedIn || intersectionCondition) {
            return;
        }

        hasFired.current = true;
        sendExposureEvent(experiments, flagKey, sendEvent);
    }, [experiments, flagKey, isInView, isLoggedIn, sendEvent, shouldFireWhenIntersects]);

    return shouldFireWhenIntersects ? <div ref={ref} aria-hidden="true" /> : null;
};
